/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import type { AxiosResponse } from "axios";
import { FailoverResponse, MaintenanceResponse, ServiceStatus, UsageData } from "../Types";
import { getFailoverHistoryAPI, getMonitoringAPI, getServiceStatusAPI, getServicesUsageAPI } from "kontrolEdge/views/requests/request";
import { readCachedFetch, writeCachedFetch } from "@hooks/useCachedFetch";

interface ManagerOverviewLoadingState {
  service: boolean;
  usage: boolean;
  failover: boolean;
  maintenance: boolean;
}

interface ManagerOverviewDataState {
  serviceStatus: ServiceStatus | null;
  usage: UsageData | null;
  failover: FailoverResponse | null;
  maintenance: MaintenanceResponse | null;
}

const EMPTY_OVERVIEW: ManagerOverviewDataState = {
  serviceStatus: null,
  usage: null,
  failover: null,
  maintenance: null,
};

const ALL_LOADING: ManagerOverviewLoadingState = { service: true, usage: true, failover: true, maintenance: true };
const NOTHING_LOADING: ManagerOverviewLoadingState = { service: false, usage: false, failover: false, maintenance: false };

/**
 * Whether the tenant has any service at all. Every bucket the endpoint returns counts: a
 * BBaaS tenant whose only services are ports and SD-WAN hubs is not an empty tenant, and
 * leaving those two out rendered the empty state over a fully provisioned dashboard.
 */
function hasAnyService(serviceStatus: ServiceStatus | null): boolean {
  return (
    !!serviceStatus &&
    (
      (serviceStatus.ports?.length ?? 0) > 0 ||
      (serviceStatus.internet?.length ?? 0) > 0 ||
      (serviceStatus.datacenterInterconnect?.length ?? 0) > 0 ||
      (serviceStatus.cloudConnectivity?.length ?? 0) > 0 ||
      (serviceStatus.fwaas?.length ?? 0) > 0 ||
      (serviceStatus.sdwan?.length ?? 0) > 0
    )
  );
}

/** The value of a settled request, or — when it failed — the last good one. */
function settledOr<T>(
  result: PromiseSettledResult<AxiosResponse>,
  pick: (response: AxiosResponse) => T | null,
  fallback: T | null
): T | null {
  return result.status === "fulfilled" ? pick(result.value) : fallback;
}

export function useManagerOverviewData(api: any, tenantId: number) {
  // The last assembled overview for this tenant paints at once when the user comes back (a
  // view switch, Back); the four requests below still run and replace it. The overview's
  // only caller is ManagerOverview, so the cache lives here.
  const cacheKey = tenantId ? `kontroledge/overview?tenantId=${tenantId}` : null;

  const [data, setData] = useState<ManagerOverviewDataState>(
    () => readCachedFetch<ManagerOverviewDataState>(cacheKey) ?? EMPTY_OVERVIEW
  );
  const [isServiceFetched, setIsServiceFetched] = useState(
    () => readCachedFetch(cacheKey) !== undefined
  );
  // Starts loading when there is nothing to paint: the first frame would otherwise read as
  // an empty tenant before the fetch effect has had a chance to run.
  const [loading, setLoading] = useState<ManagerOverviewLoadingState>(() =>
    cacheKey && readCachedFetch(cacheKey) === undefined ? ALL_LOADING : NOTHING_LOADING
  );
  /** A cached overview is on screen and a newer one is on its way. */
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Whether the service-status request itself failed, as opposed to coming
   * back empty. Both used to collapse into `null` here, so a backend outage
   * rendered the "your tenant is currently empty" state to a customer with
   * dozens of provisioned services. The two need to stay distinguishable all
   * the way to the view.
   */
  const [hasServiceError, setHasServiceError] = useState(false);

  useEffect(() => {
    if (!cacheKey) return;

    const controller = new AbortController();
    const { signal } = controller;

    // Another tenant's overview never shows under this one: its cached answer, or skeletons.
    const cached = readCachedFetch<ManagerOverviewDataState>(cacheKey);
    setHasServiceError(false);
    // Set on both branches: an aborted run (the tenant moved) never reaches its `finally`.
    setIsRefreshing(Boolean(cached));
    if (cached) {
      setData(cached);
      setLoading(NOTHING_LOADING);
      setIsServiceFetched(true);
    } else {
      setData(EMPTY_OVERVIEW);
      setLoading(ALL_LOADING);
      setIsServiceFetched(false);
    }

    (async () => {
      try {
        const pService = getServiceStatusAPI(api, tenantId, { signal });
        const pUsage = getServicesUsageAPI(api, tenantId, { signal });
        const pFail  = getFailoverHistoryAPI(api, tenantId, { signal });
        const pMaint = getMonitoringAPI(api, tenantId, { signal });

        const [rService, rUsage, rFail, rMaint] = await Promise.allSettled([
          pService, pUsage, pFail, pMaint
        ]);

        // Extract values safely. A request that failed on a refresh keeps the section it
        // feeds on its last good answer instead of blanking it.
        const fresh: ManagerOverviewDataState = {
          serviceStatus: settledOr(rService, (r) => r.data?.data ?? null, cached?.serviceStatus ?? null),
          usage: settledOr(rUsage, (r) => r.data ?? null, cached?.usage ?? null),
          failover: settledOr(rFail, (r) => r.data?.data ?? null, cached?.failover ?? null),
          maintenance: settledOr(rMaint, (r) => r.data?.data ?? null, cached?.maintenance ?? null),
        };

        // Single, clean state commit with the full payload
        if (!signal.aborted) {
          // Only an overview the page can stand on is cached: a failed service-status
          // request with nothing to fall back on is "monitoring unavailable", which the
          // next visit must ask again rather than repaint.
          if (rService.status === "fulfilled" || cached) writeCachedFetch(cacheKey, fresh);
          setHasServiceError(rService.status === "rejected" && !cached);
          setData(fresh);
          setIsServiceFetched(true);
        }

        // Optional: log partial failures
        if (rService.status === "rejected") console.error("serviceStatus failed:", rService.reason);
        if (rUsage.status   === "rejected") console.error("usage failed:", rUsage.reason);
        if (rFail.status    === "rejected") console.error("failover failed:", rFail.reason);
        if (rMaint.status   === "rejected") console.error("maintenance failed:", rMaint.reason);
      } catch (err) {
        if (!signal.aborted) console.error("ManagerOverview parallel fetch error:", err);
      } finally {
        if (!signal.aborted) {
          setLoading(NOTHING_LOADING);
          setIsRefreshing(false);
        }
      }
    })();

    return () => controller.abort();
  }, [api, tenantId, cacheKey]);

  const hasData = hasAnyService(data.serviceStatus);
  return { data, loading, hasData, isServiceFetched, hasServiceError, isRefreshing };
}
